import { useState, useEffect, useCallback } from 'react';
import { fetchCustomers, Customer, CustomerResponse, CustomerParams } from '../api/customer';

interface UseCustomersResult {
  customers: Customer[];
  loading: boolean;
  error: string | null;
  searchQuery: string;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  paginatedCustomers: Customer[];
  setSearchQuery: (query: string) => void;
  setCurrentPage: (page: number) => void;
  goToNextPage: () => void;
  goToPreviousPage: () => void;
  refetch: () => Promise<void>;
}

export const useCustomers = (pageSize: number = 10, initialSearchQuery: string = ''): UseCustomersResult => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>(initialSearchQuery);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: CustomerParams = {
        ...(searchQuery && { searchQuery }),
        pageNumber: currentPage,
        pageSize,
      };
      const response: CustomerResponse = await fetchCustomers(params);
      if (response && response.response) {
        setCustomers(response.response);
        // Set total count from API response if available
        setTotalCount(response.totalCount || response.response.length);
      }
    } catch (err) {
      console.error('Failed to fetch customers:', err);
      setError((err as Error).message || 'Failed to load customers');
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, currentPage, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (initialSearchQuery && initialSearchQuery !== searchQuery) {
      setSearchQuery(initialSearchQuery);
    }
  }, [initialSearchQuery]);

  // Calculate pagination based on server response
  const totalPages = Math.ceil(totalCount / pageSize);
  
  // Use customers directly from API (already paginated on server)
  const paginatedCustomers = customers;

  const handleSetSearchQuery = useCallback((query: string) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page on search
  }, []);

  const handleSetCurrentPage = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const goToNextPage = useCallback(() => {
    setCurrentPage((prev) => prev + 1);
  }, []);

  const goToPreviousPage = useCallback(() => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  }, []);

  const refetch = async () => {
    await fetchData();
  };

  return {
    customers,
    loading,
    error,
    searchQuery,
    currentPage,
    totalPages,
    pageSize,
    paginatedCustomers,
    setSearchQuery: handleSetSearchQuery,
    setCurrentPage: handleSetCurrentPage,
    goToNextPage,
    goToPreviousPage,
    refetch,
  };
};
